import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { useOrganizersStore } from '../../stores/organizersStore';
import { Users, Check, AlertTriangle, Globe, Upload } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import { toast } from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (excludedOrganizerIds: string[], excludedCountries: string[]) => void;
};

export const ExcludeOrganizersModal = ({ isOpen, onClose, onConfirm }: Props) => {
  const [selectedOrganizerIds, setSelectedOrganizerIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<'organizers' | 'countries' | 'excel'>('organizers');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const { organizers, loading, fetchOrganizers } = useOrganizersStore();
  const { translations } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      fetchOrganizers();
      fetchCountries();
    }
  }, [isOpen, fetchOrganizers]);

  const fetchCountries = async () => {
    try {
      // Get unique countries from PilgrimGuideLinks
      const linksRef = collection(firestore, 'PilgrimGuideLinks');
      const linksSnapshot = await getDocs(linksRef);
      
      const uniqueCountries = new Set<string>();
      linksSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.residenceCountry) {
          uniqueCountries.add(data.residenceCountry);
        }
      });

      setCountries(Array.from(uniqueCountries).sort());
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const handleToggleOrganizer = (organizerId: string) => {
    setSelectedOrganizerIds(prev => 
      prev.includes(organizerId) 
        ? prev.filter(id => id !== organizerId)
        : [...prev, organizerId]
    );
  };

  const handleToggleCountry = (country: string) => {
    setSelectedCountries(prev => 
      prev.includes(country) 
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const handleSelectAll = () => {
    if (selectedTab === 'organizers') {
      const allOrganizerIds = Object.values(organizers).map(org => org.id);
      setSelectedOrganizerIds(allOrganizerIds);
    } else if (selectedTab === 'countries') {
      setSelectedCountries([...countries]);
    }
  };

  const handleDeselectAll = () => {
    if (selectedTab === 'organizers') {
      setSelectedOrganizerIds([]);
    } else if (selectedTab === 'countries') {
      setSelectedCountries([]);
    } else if (selectedTab === 'excel') {
      setFile(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = () => {
    if (selectedTab === 'excel') {
      if (!file) {
        toast.error('Please select an Excel file');
        return;
      }
      onConfirm([], []);
    } else {
      onConfirm(selectedOrganizerIds, selectedCountries);
    }
  };

  const filteredOrganizers = Object.values(organizers)
    .filter(org => org.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredCountries = countries
    .filter(country => country.toLowerCase().includes(countrySearchTerm.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={translations?.accommodationTable?.excludeOrganizers || 'Exclude from Removal'}
    >
      <div className="space-y-4">
        <div className="bg-yellow-50 p-4 rounded-lg flex items-start gap-3">
          <AlertTriangle className="text-yellow-600 shrink-0 mt-0.5" size={20} />
          <p className="text-yellow-700 text-sm">
            {translations?.accommodationTable?.excludeOrganizersInfo || 
            'Select organizers or countries whose pilgrims should be excluded from the removal process. Assignments from selected items will be preserved.'}
          </p>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b mb-4">
          <button
            onClick={() => setSelectedTab('organizers')}
            className={`flex-1 py-3 text-center font-medium transition-colors relative
              ${selectedTab === 'organizers' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <div className="flex items-center justify-center gap-2">
              <Users size={20} />
              <span>Organizers</span>
            </div>
            {selectedTab === 'organizers' && (
              <motion.div 
                layoutId="activeTab" 
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" 
              />
            )}
          </button>
          <button
            onClick={() => setSelectedTab('countries')}
            className={`flex-1 py-3 text-center font-medium transition-colors relative
              ${selectedTab === 'countries' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <div className="flex items-center justify-center gap-2">
              <Globe size={20} />
              <span>Countries</span>
            </div>
            {selectedTab === 'countries' && (
              <motion.div 
                layoutId="activeTab" 
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" 
              />
            )}
          </button>
          <button
            onClick={() => setSelectedTab('excel')}
            className={`flex-1 py-3 text-center font-medium transition-colors relative
              ${selectedTab === 'excel' ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <div className="flex items-center justify-center gap-2">
              <Upload size={20} />
              <span>Excel Upload</span>
            </div>
            {selectedTab === 'excel' && (
              <motion.div 
                layoutId="activeTab" 
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" 
              />
            )}
          </button>
        </div>

        {selectedTab === 'organizers' && (
          <>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={translations?.accommodationTable?.searchOrganizers || 'Search organizers...'}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-700">
                {selectedOrganizerIds.length} {translations?.accommodationTable?.selectedOrganizers || 'organizers selected'}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  {translations?.accommodationTable?.selectAll || 'Select All'}
                </button>
                <button
                  onClick={handleDeselectAll}
                  className="px-3 py-1 text-xs text-gray-600 hover:text-gray-700"
                >
                  {translations?.accommodationTable?.deselectAll || 'Deselect All'}
                </button>
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto border rounded-lg">
              {loading ? (
                <div className="p-4 text-center text-gray-500">
                  {translations?.accommodationTable?.loadingOrganizers || 'Loading organizers...'}
                </div>
              ) : filteredOrganizers.length > 0 ? (
                <div className="divide-y">
                  {filteredOrganizers.map(organizer => (
                    <motion.div
                      key={organizer.id}
                      whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                      className={`
                        p-3 flex items-center justify-between cursor-pointer ${
                        selectedOrganizerIds.includes(organizer.id) ? 'bg-primary-50' : ''
                      }`}
                      onClick={() => handleToggleOrganizer(organizer.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-primary-50">
                          <Users className="text-primary-500" size={16} />
                        </div>
                        <div className="font-medium">{organizer.name}</div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                        selectedOrganizerIds.includes(organizer.id) 
                          ? 'border-primary-500 bg-primary-500' 
                          : 'border-gray-300'
                      }`}>
                        {selectedOrganizerIds.includes(organizer.id) && (
                          <Check className="text-white" size={12} />
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm 
                    ? (translations?.accommodationTable?.noOrganizersFound || 'No organizers match your search')
                    : (translations?.accommodationTable?.noOrganizers || 'No organizers available')}
                </div>
              )}
            </div>
          </>
        )}

        {selectedTab === 'countries' && (
          <>
            <div className="relative">
              <input
                type="text"
                value={countrySearchTerm}
                onChange={(e) => setCountrySearchTerm(e.target.value)}
                placeholder="Search countries..."
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-700">
                {selectedCountries.length} countries selected
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  Select All
                </button>
                <button
                  onClick={handleDeselectAll}
                  className="px-3 py-1 text-xs text-gray-600 hover:text-gray-700"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto border rounded-lg">
              {countries.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  Loading countries...
                </div>
              ) : filteredCountries.length > 0 ? (
                <div className="divide-y">
                  {filteredCountries.map(country => (
                    <motion.div
                      key={country}
                      whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                      className={`
                        p-3 flex items-center justify-between cursor-pointer ${
                        selectedCountries.includes(country) ? 'bg-primary-50' : ''
                      }`}
                      onClick={() => handleToggleCountry(country)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-primary-50">
                          <Globe className="text-primary-500" size={16} />
                        </div>
                        <div className="font-medium">{country}</div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                        selectedCountries.includes(country) 
                          ? 'border-primary-500 bg-primary-500' 
                          : 'border-gray-300'
                      }`}>
                        {selectedCountries.includes(country) && (
                          <Check className="text-white" size={12} />
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  {countrySearchTerm 
                    ? 'No countries match your search' 
                    : 'No countries available'}
                </div>
              )}
            </div>
          </>
        )}

        {selectedTab === 'excel' && (
          <div className="space-y-3">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-blue-700 text-sm">
                Upload an Excel file containing passport numbers of pilgrims to be removed from this accommodation. The file should have a column named "passport" with the passport numbers.
              </p>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-300 transition-colors">
              <input
                type="file"
                id="excel-upload"
                className="hidden"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
              />
              <label htmlFor="excel-upload" className="cursor-pointer">
                <Upload size={36} className="mx-auto mb-2 text-gray-400" />
                <p className="text-sm font-medium text-primary-600">Click to select file</p>
                <p className="text-xs text-gray-500 mt-1">or drag and drop</p>
              </label>
              {file && (
                <div className="mt-4">
                  <p className="text-sm text-green-600 font-medium">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              )}
            </div>

            <div className="text-sm text-gray-600 p-3 bg-gray-50 rounded-md">
              <p className="font-medium mb-2">Recommendations:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Ensure your Excel file has a 'passport' column</li>
                <li>The system will find and remove pilgrims with matching passport numbers</li>
                <li>Pilgrims not found will be reported in the results</li>
              </ul>
            </div>
            
            <button
              onClick={handleDeselectAll}
              className="text-xs text-red-600 hover:text-red-700"
            >
              Remove file
            </button>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.common?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            {translations?.accommodationTable?.continueRemoval || 'Continue with Removal'}
          </button>
        </div>
      </div>
    </Modal>
  );
};